<!-- src/components/settings/groups/PlanUsageSection.vue -->
<!-- Sysadmin-only: plan/limits editing plus current usage, rendered inside the
     sysadmin settings collapsible of the account management view. -->
<template>
  <div v-if="visible" class="settings-panel plan-usage-section mb-4">
    <h3 class="section-title">{{ $t('planLimits.admin.planAndLimits') }}</h3>
    <template v-if="!isLegacy">
      <p class="section-intro current-plan">
        {{ $t('planLimits.usage.currentPlan') }}:
        <strong>{{ planUsage.plan ? planUsage.plan.name : $t('planLimits.usage.customLimits') }}</strong>
      </p>
      <div class="usage mb-4">
        <div class="usage-row">
          <div class="usage-head">
            <span>{{ $t('planLimits.usage.projects') }}</span>
            <span class="usage-value">
              {{ planUsage.projectCount }} / {{ formatLimit(accountLimits.limit_projects) }}
            </span>
          </div>
          <b-progress
            v-if="accountLimits.limit_projects !== null"
            :value="planUsage.projectCount"
            :max="accountLimits.limit_projects"
            :variant="barVariant(planUsage.projectCount, accountLimits.limit_projects)"
            height="6px"
          />
        </div>
        <div class="usage-row">
          <div class="usage-head">
            <span>{{ $t('planLimits.usage.users') }}</span>
            <span class="usage-value">{{ planUsage.userCount }} / {{ formatLimit(accountLimits.limit_users) }}</span>
          </div>
          <b-progress
            v-if="accountLimits.limit_users !== null"
            :value="planUsage.userCount"
            :max="accountLimits.limit_users"
            :variant="barVariant(planUsage.userCount, accountLimits.limit_users)"
            height="6px"
          />
        </div>
        <div class="usage-row">
          <div class="usage-head">
            <span>{{ $t('planLimits.usage.pagesPerProject') }}</span>
            <span class="usage-value">{{ formatLimit(accountLimits.limit_pages_per_project) }}</span>
          </div>
        </div>
      </div>
    </template>
    <div class="settings-subpanel">
      <b-form-group :label="$t('planLimits.admin.plan')" label-for="plan-select">
        <b-form-select
          id="plan-select"
          v-model="editPlanId"
          :options="planOptions"
          @change="onPlanSelected"
        />
      </b-form-group>
      <h4 class="block-title mt-4">{{ $t('planLimits.admin.overrides') }}</h4>
      <b-row>
        <b-col sm="4">
          <b-form-group :label="$t('planLimits.usage.projects')" label-for="override-projects">
            <b-form-input id="override-projects" v-model.number="editOverrideProjects" type="number" min="0" />
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group :label="$t('planLimits.usage.pagesPerProject')" label-for="override-pages">
            <b-form-input id="override-pages" v-model.number="editOverridePages" type="number" min="0" />
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group :label="$t('planLimits.usage.users')" label-for="override-users">
            <b-form-input id="override-users" v-model.number="editOverrideUsers" type="number" min="0" />
          </b-form-group>
        </b-col>
      </b-row>
      <div class="d-flex align-items-center justify-content-between">
        <b-form-text class="mt-0">{{ $t('planLimits.admin.overridesHint') }}</b-form-text>
        <b-button class="green-button save-button" :disabled="savingPlan" @click="savePlan">
          {{ $t('planLimits.admin.save') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import GetAllPlans from '@/graphQlQueries/queries/getAllPlans';
import UpdateGroupPlan from '@/graphQlQueries/mutations/updateGroupPlan';
import UpdateGroupSettings from '@/graphQlQueries/mutations/updateGroupSettings';
import AccountLimitsMixin from '@/mixins/accountLimits';
import { applyPlanFeatures } from '@/utils/accountLimits';

const OVERRIDE_KEYS = [
  ['editOverrideProjects', 'limit_projects'],
  ['editOverridePages', 'limit_pages_per_project'],
  ['editOverrideUsers', 'limit_users'],
];

export default {
  name: 'PlanUsageSection',
  mixins: [AccountLimitsMixin],
  props: {
    groupId: { type: Number, required: true },
    isSysAdmin: { type: Boolean, default: false },
    groupSettings: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      plans: [],
      editPlanId: null,
      editOverrideProjects: null,
      editOverridePages: null,
      editOverrideUsers: null,
      savingPlan: false,
    };
  },
  apollo: {
    plans: {
      query: GetAllPlans,
      skip() {
        return !this.isSysAdmin;
      },
    },
  },
  computed: {
    visible() {
      return this.isSysAdmin && this.planUsage !== null;
    },
    isLegacy() {
      return !this.planUsage.plan && !this.planUsage.limitsOverride;
    },
    planOptions() {
      const base = [{ value: null, text: this.$t('planLimits.admin.noPlan') }];
      return base.concat((this.plans || []).map((plan) => ({
        value: plan.plan_id,
        text: plan.name,
      })));
    },
  },
  watch: {
    // Initialize the edit form from the loaded group state (initial load and
    // refetch after save). Not triggered by user edits.
    planUsage: {
      immediate: true,
      handler(usage) {
        if (!usage || !this.isSysAdmin) return;
        this.editPlanId = usage.plan ? usage.plan.plan_id : null;
        const override = usage.limitsOverride || {};
        const plan = usage.plan || {};
        OVERRIDE_KEYS.forEach(([field, key]) => {
          // Show the effective value: override wins, otherwise the plan's
          // default — so the inputs always spell out what currently applies.
          if (override[key] !== undefined && override[key] !== null) {
            this[field] = override[key];
          } else if (plan[key] !== undefined && plan[key] !== null) {
            this[field] = plan[key];
          } else {
            this[field] = null;
          }
        });
      },
    },
  },
  methods: {
    formatLimit(limit) {
      return limit === null ? '∞' : String(limit);
    },
    barVariant(count, limit) {
      if (count >= limit) return 'danger';
      if (limit > 0 && count / limit >= 0.8) return 'warning';
      return 'primary';
    },
    // Prefill the inputs with the selected plan's limits so the sysadmin sees
    // what the plan means. Values equal to the plan default are NOT stored as
    // overrides on save (see buildOverride).
    onPlanSelected(planId) {
      const plan = (this.plans || []).find((p) => p.plan_id === planId) || {};
      OVERRIDE_KEYS.forEach(([field, key]) => {
        this[field] = plan[key] !== undefined && plan[key] !== null ? plan[key] : null;
      });
    },
    buildOverride() {
      const chosenPlan = (this.plans || []).find((p) => p.plan_id === this.editPlanId) || {};
      const override = {};
      OVERRIDE_KEYS.forEach(([field, key]) => {
        const value = this[field];
        // Only non-negative integers: limits_override is jsonb and
        // group_effective_limit() casts with `::integer` on every insert —
        // a non-integer value would make that cast throw and hard-lock the group.
        if (!Number.isInteger(value) || value < 0) return;
        // Values identical to the selected plan's default are not overrides —
        // storing them would freeze the account against future plan changes.
        if (chosenPlan[key] === value) return;
        override[key] = value;
      });
      return Object.keys(override).length ? override : null;
    },
    async savePlan() {
      this.savingPlan = true;
      try {
        const previousPlanId = this.planUsage && this.planUsage.plan
          ? this.planUsage.plan.plan_id
          : null;

        await this.$apollo.mutate({
          mutation: UpdateGroupPlan,
          variables: {
            group_id: this.groupId,
            plan_id: this.editPlanId,
            limits_override: this.buildOverride(),
          },
        });

        // On plan change, merge the new plan's features into the group's
        // existing settings (do not overwrite unrelated flags). Isolated in
        // its own try/catch: the plan itself WAS saved above, so a failure
        // here is reported specifically and must not skip the refetch below.
        if (this.editPlanId && this.editPlanId !== previousPlanId) {
          try {
            const chosenPlan = (this.plans || []).find((p) => p.plan_id === this.editPlanId);
            if (chosenPlan && Object.keys(chosenPlan.features || {}).length) {
              await this.$apollo.mutate({
                mutation: UpdateGroupSettings,
                variables: {
                  group_id: this.groupId,
                  settings: applyPlanFeatures(this.groupSettings, chosenPlan.features),
                },
              });
            }
          } catch (featuresError) {
            console.error('apply plan features error', featuresError);
            this.$bvToast.toast(
              this.$t('planLimits.admin.featuresApplyFailed'),
              {
                title: this.$t('planLimits.limitReachedTitle'),
                variant: 'danger',
                solid: true,
              },
            );
          }
        }
        this.refetchPlanUsage();
        this.$bvToast.toast(this.$t('planLimits.admin.saved'), { variant: 'success', solid: true });
      } catch (error) {
        this.$bvToast.toast(String(error.message || error), { variant: 'danger', solid: true });
      } finally {
        this.savingPlan = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import './settingsPanel';

.current-plan strong {
  color: #fff;
}

.usage {
  max-width: 480px;
}

.usage-row + .usage-row {
  margin-top: 12px;
}

.usage-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  color: $settings-form-text;
  font-size: 14px;
}

.usage-value {
  color: #fff;
  font-variant-numeric: tabular-nums;
}

.progress {
  background-color: rgba(255, 255, 255, 0.1);
}

.save-button {
  min-width: 140px;
  margin-left: 16px;
}
</style>
